import * as Yup from 'yup';

import {
  isValidCPF,
  isValidCNPJ,
  isValidEmail,
  isValidPhone,
  isValidPixKey,
} from '@shared/utils/validators';

// Extend Yup string schema with custom methods
declare module 'yup' {
  interface StringSchema {
    cpf(message?: string): StringSchema;
    cnpj(message?: string): StringSchema;
    phone(message?: string): StringSchema;
  }
}

// Custom Yup validators for Brazilian documents
Yup.addMethod(Yup.string, 'cpf', function (message?: string) {
  return this.test('cpf', message ?? 'CPF inválido', function (value) {
    const { path, createError } = this;
    return !value || isValidCPF(value) || createError({ path, message: message ?? 'CPF inválido' });
  });
});

Yup.addMethod(Yup.string, 'cnpj', function (message?: string) {
  return this.test('cnpj', message ?? 'CNPJ inválido', function (value) {
    const { path, createError } = this;
    return (
      !value || isValidCNPJ(value) || createError({ path, message: message ?? 'CNPJ inválido' })
    );
  });
});

Yup.addMethod(Yup.string, 'phone', function (message?: string) {
  return this.test('phone', message ?? 'Telefone inválido', function (value) {
    const { path, createError } = this;
    return (
      !value ||
      isValidPhone(value) ||
      createError({ path, message: message ?? 'Telefone inválido' })
    );
  });
});

// Common validation schemas
export const schemas = {
  cpf: Yup.string().required('CPF é obrigatório').test('cpf', 'CPF inválido', isValidCPF),

  cnpj: Yup.string().required('CNPJ é obrigatório').test('cnpj', 'CNPJ inválido', isValidCNPJ),

  email: Yup.string()
    .required('E-mail é obrigatório')
    .test('email', 'E-mail inválido', isValidEmail),

  phone: Yup.string()
    .required('Telefone é obrigatório')
    .test('phone', 'Telefone inválido', isValidPhone),

  password: Yup.string()
    .required('Senha é obrigatória')
    .min(8, 'Senha deve ter no mínimo 8 caracteres')
    .matches(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula')
    .matches(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
    .matches(/[0-9]/, 'Senha deve conter pelo menos um número')
    .matches(/[@$!%*?&#]/, 'Senha deve conter pelo menos um caractere especial'),

  currency: Yup.number()
    .required('Valor é obrigatório')
    .min(0, 'Valor deve ser maior ou igual a zero')
    .typeError('Valor inválido'),

  date: Yup.date().required('Data é obrigatória').typeError('Data inválida'),

  pixKey: (type: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'RANDOM') =>
    Yup.string()
      .required('Chave PIX é obrigatória')
      .test('pix', 'Chave PIX inválida', (value) => (value ? isValidPixKey(value, type) : false)),
};

// Common form validation schemas
export const loginSchema = Yup.object({
  email: schemas.email,
  password: Yup.string().required('Senha é obrigatória'),
});

export const registerSchema = Yup.object({
  name: Yup.string().required('Nome é obrigatório'),
  email: schemas.email,
  cpf: schemas.cpf,
  phone: schemas.phone,
  password: schemas.password,
  confirmPassword: Yup.string()
    .required('Confirmação de senha é obrigatória')
    .oneOf([Yup.ref('password')], 'As senhas não coincidem'),
});

export const accountSchema = Yup.object({
  accountNumber: Yup.string()
    .required('Número da conta é obrigatório')
    .matches(/^\d+$/, 'Número da conta deve conter apenas dígitos'),
  accountType: Yup.string()
    .required('Tipo de conta é obrigatório')
    .oneOf(['CHECKING', 'SAVINGS'], 'Tipo de conta inválido'),
  balance: schemas.currency,
});

export const transactionSchema = Yup.object({
  amount: schemas.currency.moreThan(0, 'Valor deve ser maior que zero'),
  description: Yup.string().required('Descrição é obrigatória').max(255),
  type: Yup.string()
    .required('Tipo de transação é obrigatório')
    .oneOf(['DEPOSIT', 'WITHDRAWAL', 'TRANSFER', 'PAYMENT'], 'Tipo inválido'),
  date: schemas.date,
});
